import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ENTITLEMENT_KEYS} from '@packages/systems/billing/common';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {ONLY_ON_PRIMARY_LOCALE} from './localization';

export const canAccessWorkspaceLibraries = defineAbilityConfig([
  {
    siteModes: {
      design: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
  },
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    abilities: {isInPageBuildMode: true},
  },
] as const);

export const canManageWorkspaceLibraries = defineAbilityConfig({
  abilities: {canAccessWorkspaceLibraries: true},
  permission: ['manage', 'siteLibrary'],
  branches: {
    main: true,
  },
  siteModes: {
    design: true,
  },
} as const);

export const canShareWorkspaceLibraries = defineAbilityConfig({
  abilities: {canAccessWorkspaceLibraries: true},
  permission: ['share', 'siteLibrary'],
  siteModes: {
    design: true,
  },
} as const);

export const canAddWorkspaceLibrariesComponent = defineAbilityConfig({
  abilities: {canAccessWorkspaceLibraries: true},
  permission: ['addComponent', 'siteLibrary'],
} as const);

export const canViewWorkspaceLibrariesComponents = defineAbilityConfig([
  {abilities: {canAddWorkspaceLibrariesComponent: true}},
  {abilities: {isInReadOnlyMode: true, canAccessComponentsPanel: true}},
] as const);

export const canRecoverOrphanedLibraryContent = defineAbilityConfig({
  featureFlags: {[FEATURE_FLAGS.LIBRARY_ORPHANED_CONTENT_RECOVERY]: true},
  abilities: {canAccessWorkspaceLibraries: true},
  permission: ['manage', 'siteLibrary'],
  siteModes: {
    design: true,
  },
} as const);

/**
 * We define this as a separate ability because we skip the entitlement
 * check when a library was previously shared (and we're only sharing new updates)
 */
export const isWithinMaxWorkspaceLibrariesLimit = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.MAX_WORKSPACE_LIBRARIES],
} as const);

export const canUseCodeComponents = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_CODE_COMPONENTS],
} as const);

export const canManageCodeLibraries = defineAbilityConfig({
  abilities: {canManageWorkspaceLibraries: true, canUseCodeComponents: true},
} as const);

export const canAccessLibrariesPanel = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  branches: {
    main: true,
  },
  siteModes: {design: true},
  workflows: {
    canvas: true,
  },
  localization: ONLY_ON_PRIMARY_LOCALE,
} as const);

export const canUseLibraryPushUpdates = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_LIBRARY_PUSH_UPDATES],
} as const);

// Reordering tag styles or toggling them active/inactive is a site-wide
// change, so it is only offered on main.
export const canManageLibraryTagStyles = defineAbilityConfig({
  branches: {
    main: true,
  },
} as const);
